import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  EmptyState, ListTable, MetricCard, Panel, SearchField, SegmentedControl, StatusChip, type ListColumn,
} from '../../../ui-kit';
import {
  downloadChecks, fetchChecks, watchChecks, type PcCheckCard,
} from '../../../services/policyCheckingService';
import { fromNow, matches, useLive, when } from '../rocco/live';
import ListDownload from '../rocco/ListDownload';
import CheckStoryModal from './CheckStory';
import { money, resultChip } from './status';

const FILTERS: { value: string; label: string; test: (c: PcCheckCard) => boolean }[] = [
  { value: 'all', label: 'All', test: () => true },
  { value: 'must_fix', label: 'Must fix', test: (c) => c.result === 'must_fix' },
  { value: 'review', label: 'Review', test: (c) => c.result === 'review' },
  { value: 'all_clear', label: 'All clear', test: (c) => c.result === 'all_clear' },
];

/**
 * Every policy checked, newest first: what it was, how it came out, and how
 * much was looked at. Updates by itself while documents are being read, so a
 * check moves from "Reading the documents" to its result in place.
 */
export default function ChecksPage() {
  const { data, error, loading, streaming, reconnecting } = useLive(fetchChecks, 5000, { watch: watchChecks });
  const [open, setOpen] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [show, setShow] = React.useState('all');
  const all = data?.checks ?? [];
  const chosen = FILTERS.find((f) => f.value === show) ?? FILTERS[0];
  const rows = all.filter(chosen.test).filter((c) => matches(query, [
    c.policy_num, c.carrier, c.product, c.insured, c.stage, c.by,
  ]));

  const columns: ListColumn<PcCheckCard>[] = [
    {
      key: 'policy',
      sortValue: (c) => c.policy_num,
      header: 'Policy',
      grow: 1,
      minWidth: 150,
      render: (c) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {c.policy_num || 'Not read yet'}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            {[c.carrier, c.product].filter(Boolean).join(' · ')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'insured',
      sortValue: (c) => c.insured,
      header: 'Insured',
      width: 140,
      render: (c) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{c.insured || '—'}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, fontVariantNumeric: 'tabular-nums' })}>
            {c.face_amount != null ? money(c.face_amount) : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'started',
      sortValue: (c) => c.created_at, sortStartsDescending: true,
      header: 'Started',
      width: 130,
      render: (c) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontVariantNumeric: 'tabular-nums' })}>
            {when(c.created_at)}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{fromNow(c.created_at)}</Typography>
        </Stack>
      ),
    },
    {
      key: 'documents',
      sortValue: (c) => c.document_count, sortStartsDescending: true,
      header: 'Documents',
      width: 150,
      render: (c) => (
        <Stack>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {c.document_count} read
          </Typography>
          {c.missing.length ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              No {c.missing.map((k) => (k === 'policy' ? 'issued policy' : k)).join(' or ')}
            </Typography>
          ) : (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>All three</Typography>
          )}
        </Stack>
      ),
    },
    {
      key: 'result',
      sortValue: (c) => `${c.must_fix ? 0 : c.review ? 1 : 2}${c.stage}`,
      header: 'Result',
      grow: 0.8,
      minWidth: 190,
      render: (c) => (
        <Stack spacing={0.25} alignItems="flex-start">
          <StatusChip {...resultChip(c)} />
          {c.status === 'checked' ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {c.must_fix + c.review
                ? `${[c.must_fix && `${c.must_fix} to fix`, c.review && `${c.review} to review`]
                  .filter(Boolean).join(', ')} of ${c.must_fix + c.review + c.matched}`
                : `${c.matched} fields agree`}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'by',
      sortValue: (c) => c.by,
      header: 'Started by',
      width: 130,
      render: (c) => (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary })}>{c.by || '—'}</Typography>
      ),
    },
  ];

  const table = (
    <ListTable
      columns={columns}
      rows={rows}
      getRowKey={(c) => c.id}
      pageSize={25}
      loading={loading}
      defaultSort={{ key: 'started', direction: 'desc' }}
      onRowClick={(c) => setOpen(c.id)}
      emptyMessage={all.length
        ? 'Nothing matches. Clear the search or the filter.'
        : 'No checks yet. Open New check and drop in a policy’s documents, or run a sample set.'}
    />
  );
  // Search beside the title, the switch on the left of the toolbar, the
  // download on its right (the panel header's two rows, 21 Oct 2026).
  const searchBox = <SearchField value={query} onChange={setQuery} placeholder="Search checks…" />;
  const picker = (
    <SegmentedControl
      value={chosen.value}
      onChange={setShow}
      ariaLabel="Which checks"
      options={FILTERS.map((f) => ({ value: f.value, label: `${f.label} · ${all.filter(f.test).length}` }))}
    />
  );
  const download = <ListDownload download={downloadChecks} total={all.length} noun="checks" />;

  const checked = all.filter((c) => c.status === 'checked');
  const caught = checked.reduce((sum, c) => sum + c.must_fix, 0);
  const looked = checked.reduce((sum, c) => sum + c.must_fix + c.review + c.matched, 0);
  const state = reconnecting ? 'Reconnecting…' : streaming ? 'live' : 'updates by itself';

  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
        <MetricCard loading={loading} label="Policies checked by AI" value={all.length.toLocaleString()}
          info="Policies checked in this workspace." />
        <MetricCard loading={loading} label="Must fix" value={checked.filter((c) => c.result === 'must_fix').length.toLocaleString()}
          info="Checks where the policy is not what was applied for." />
        <MetricCard loading={loading} label="Things to fix" value={caught.toLocaleString()}
          info="Individual fields that must be put right, across every check." />
        <MetricCard loading={loading} label="Fields checked by AI" value={looked.toLocaleString()}
          info="Every field compared across the documents, agreeing or not." />
      </Box>

      <Panel
        title="Checks"
        subtitle={`${all.length} check${all.length === 1 ? '' : 's'} · newest first · ${state}`}
        search={searchBox}
        tools={picker}
        actions={download}
        expanded={{ content: table, actions: <>{searchBox}{picker}{download}</> }}
      >
        {error && !data ? <EmptyState message={error} /> : null}
        {error && !data ? null : table}
      </Panel>

      {open ? <CheckStoryModal checkId={open} onClose={() => setOpen(null)} /> : null}
    </Stack>
  );
}
